import React, { useEffect, useRef } from 'react';

// Utility function to safely execute regex with timeout protection
const safeRegexMatch = (input, pattern, timeout = 100) => {
  if (!input || typeof input !== 'string' || input.length > 10000) {
    return null;
  }
  
  let result = null;
  let completed = false;
  
  // Create a worker-like timeout mechanism
  const timeoutId = setTimeout(() => {
    if (!completed) {
      console.warn('Regex operation timed out, preventing potential ReDoS');
      result = null;
    }
  }, timeout);
  
  try {
    result = input.match(pattern);
    completed = true;
  } catch (error) {
    console.warn('Regex operation failed:', error);
    result = null;
  } finally {
    clearTimeout(timeoutId);
  }
  
  return result;
};

const ScriptInjector = ({ html }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!html || !containerRef.current) return;

    // Check if this HTML contains a Formstack script
    // Use safer regex patterns to prevent ReDoS attacks
    const formstackScriptMatch = safeRegexMatch(html, /<script[^>]{0,500}src="https:\/\/quantum-health\.formstack\.com\/forms\/js\.php\/([^"]{1,100})"[^>]{0,500}><\/script>/i);
    
    if (formstackScriptMatch) {
      // Extract the form path from the script URL
      const formPath = formstackScriptMatch[1];
      
      // Extract noscript content if present - limit content length to prevent ReDoS
      const noscriptMatch = safeRegexMatch(html, /<noscript>([^<]{0,1000})<\/noscript>/i);
      let title = "Online Form";
      if (noscriptMatch) {
        const titleMatch = safeRegexMatch(noscriptMatch[1], /title="([^"]{1,100})"/);
        if (titleMatch) {
          title = titleMatch[1];
        }
      }
      
      // Create the iframe HTML with auto-resize capability
      const iframeId = `formstack-iframe-${Date.now()}`;
      const iframeHtml = `<iframe id="${iframeId}" src="https://quantum-health.formstack.com/forms/${formPath}?embedded=true" width="100%" height="800" frameborder="0" scrolling="no" style="min-height: 400px; border: none;" title="${title}"></iframe><noscript><a href="https://quantum-health.formstack.com/forms/${formPath}" title="${title}">${title}</a></noscript>`;
      
      // Replace the Formstack script and noscript with the iframe
      let processedHtml = html;
      processedHtml = processedHtml.replace(formstackScriptMatch[0], iframeHtml);
      if (noscriptMatch) {
        processedHtml = processedHtml.replace(noscriptMatch[0], '');
      }
      
      // Set the processed HTML
      containerRef.current.innerHTML = processedHtml;
      
      // Add sophisticated auto-resize functionality
      const iframe = containerRef.current.querySelector(`#${iframeId}`);
      if (iframe) {
        let lastHeight = 0;
        let resizeAttempts = 0;
        const maxResizeAttempts = 10;

        // Enhanced function to resize iframe
        const resizeIframe = () => {
          resizeAttempts++;
          
          try {
            // Try to get the content height directly (may fail due to CORS)
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            if (iframeDoc && iframeDoc.body) {
              const body = iframeDoc.body;
              const html = iframeDoc.documentElement;
              
              const height = Math.max(
                body.scrollHeight,
                body.offsetHeight,
                html.clientHeight,
                html.scrollHeight,
                html.offsetHeight,
                // Also check for any form containers
                ...Array.from(iframeDoc.querySelectorAll('form, .form-container, #fsForm')).map(el => el.offsetHeight || 0)
              );
              
              if (height > 100 && height !== lastHeight) {
                iframe.style.height = height + 'px';
                lastHeight = height;
                console.log(`Formstack iframe resized to: ${height}px (direct method)`);
                return true; // Success
              }
            }
          } catch (e) {
            // Cross-origin restrictions - this is expected
          }
          
          // Alternative: Use a hidden probe iframe to measure
          if (resizeAttempts <= 3) {
            const probeIframe = document.createElement('iframe');
            probeIframe.style.position = 'absolute';
            probeIframe.style.left = '-9999px';
            probeIframe.style.width = iframe.offsetWidth + 'px';
            probeIframe.style.height = 'auto';
            probeIframe.style.border = 'none';
            probeIframe.src = iframe.src;
            
            document.body.appendChild(probeIframe);
            
            probeIframe.onload = () => {
              setTimeout(() => {
                try {
                  const probeDoc = probeIframe.contentDocument || probeIframe.contentWindow.document;
                  if (probeDoc && probeDoc.body) {
                    const height = Math.max(
                      probeDoc.body.scrollHeight,
                      probeDoc.documentElement.scrollHeight
                    );
                    
                    if (height > 100 && height !== lastHeight) {
                      iframe.style.height = height + 'px';
                      lastHeight = height;
                      console.log(`Formstack iframe resized to: ${height}px (probe method)`);
                    }
                  }
                } catch (e) {
                  // Still blocked by CORS
                }
                document.body.removeChild(probeIframe);
              }, 1000);
            };
          }
          
          return false;
        };

        // Progressive resize strategy
        iframe.onload = () => {
          // Try immediate resize
          setTimeout(() => resizeIframe(), 100);
          
          // Try after form likely loads
          setTimeout(() => resizeIframe(), 500);
          setTimeout(() => resizeIframe(), 1000);
          setTimeout(() => resizeIframe(), 2000);
          
          // Try after potential AJAX calls
          setTimeout(() => resizeIframe(), 3000);
          setTimeout(() => resizeIframe(), 5000);
        };

        // Enhanced message listening for Formstack
        const handleMessage = (event) => {
          if (event.origin === 'https://quantum-health.formstack.com' || 
              event.origin.includes('formstack.com')) {
            
            let height = null;
            
            // Handle different message formats
            if (event.data && typeof event.data === 'object') {
              // Object format: {height: 500} or {type: 'resize', height: 500}
              height = event.data.height || event.data.frameHeight || event.data.size;
            } else if (typeof event.data === 'string' && event.data.length < 100) {
              // String formats: "height:500", "resize:500", "500px", etc.
              // Limit string length and use safer regex patterns with timeout protection
              const heightMatches = [
                safeRegexMatch(event.data, /^height[:\s]*(\d{1,5})$/i),
                safeRegexMatch(event.data, /^resize[:\s]*(\d{1,5})$/i),
                safeRegexMatch(event.data, /^(\d{1,5})px$/i),
                safeRegexMatch(event.data, /^(\d{1,5})$/),
              ].find(match => match);
              
              if (heightMatches) {
                height = parseInt(heightMatches[1], 10);
              }
            } else if (typeof event.data === 'number') {
              // Direct number
              height = event.data;
            }
            
            // Validate height value to prevent malicious input
            if (height && 
                typeof height === 'number' && 
                !isNaN(height) && 
                isFinite(height) && 
                height > 100 && 
                height < 10000 && 
                height !== lastHeight) {
              iframe.style.height = height + 'px';
              lastHeight = height;
              console.log(`Formstack iframe resized to: ${height}px (message method)`);
            }
          }
        };

        window.addEventListener('message', handleMessage);
        
        // Mutation observer to detect changes in iframe content (if accessible)
        let observer;
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
          if (iframeDoc) {
            observer = new MutationObserver(() => {
              if (resizeAttempts < maxResizeAttempts) {
                setTimeout(resizeIframe, 100);
              }
            });
            observer.observe(iframeDoc.body, { 
              childList: true, 
              subtree: true, 
              attributes: true 
            });
          }
        } catch (e) {
          // CORS blocked - expected
        }
        
        // Store cleanup function on the iframe
        iframe._cleanup = () => {
          window.removeEventListener('message', handleMessage);
          if (observer) {
            observer.disconnect();
          }
        };
      }
      
      // Process any remaining scripts (non-Formstack)
      const scripts = containerRef.current.querySelectorAll('script');
      scripts.forEach((oldScript) => {
        const newScript = document.createElement('script');
        
        // Copy attributes
        Array.from(oldScript.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });
        
        // Add async attribute to avoid parser blocking
        if (newScript.src && !newScript.async && !newScript.defer) {
          newScript.async = true;
        }
        
        // Copy script content
        if (oldScript.innerHTML) {
          newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        }
        
        // Replace old script with new one
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
      
      return;
    }

    // If no Formstack script, process normally
    containerRef.current.innerHTML = html;

    // Find and execute any script tags
    const scripts = containerRef.current.querySelectorAll('script');
    
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');
      
      // Copy attributes
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      
      // Add async attribute to avoid parser blocking
      if (newScript.src && !newScript.async && !newScript.defer) {
        newScript.async = true;
      }
      
      // Copy script content
      if (oldScript.innerHTML) {
        newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      }
      
      // Replace old script with new one
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

    // Cleanup function
    return () => {
      if (containerRef.current) {
        // Clean up any Formstack iframe message listeners
        const iframes = containerRef.current.querySelectorAll('iframe[id^="formstack-iframe-"]');
        iframes.forEach(iframe => {
          if (iframe._cleanup) {
            iframe._cleanup();
          }
        });
      }
    };
  }, [html]);

  return <div ref={containerRef} />;
};

export default ScriptInjector;
